/**
 * W-dc12e842 (review pass 1, P2): a raw request or a command shown exactly as it will run. Characters that display
 * differently from what runs (direction controls such as U+202E, U+202A-202E, U+2066-2069, U+200E, U+200F, U+061C;
 * zero-width characters such as U+200B-200D, U+2060, U+FEFF; any other control or format character, and line or
 * paragraph separators) are shown as a visible marker, "⟨U+202E⟩", never applied. A newline and a tab stay as they
 * are. Every view of a raw request (the Desk's conversation card and Inbox, Pocket's approval card) renders through
 * this.
 */

// Written as escapes: a literal line or paragraph separator would end the source line.
const HIDDEN_CLASS = "[\\p{Cc}\\p{Cf}\\u2028\\u2029]";
const HIDDEN = new RegExp(HIDDEN_CLASS, "u");
const HIDDEN_ALL = new RegExp(HIDDEN_CLASS, "gu");

/** The visible marker for one hidden character: "⟨U+202E⟩". */
export const hiddenCharacterMarker = (character: string): string =>
  `⟨U+${character.codePointAt(0)!.toString(16).toUpperCase().padStart(4, "0")}⟩`;

const isHidden = (character: string): boolean =>
  character !== "\n" && character !== "\t" && HIDDEN.test(character);

/** The text with every hidden character replaced by its visible marker (newlines and tabs kept). */
export function revealedRequestText(text: string): string {
  return text.replace(HIDDEN_ALL, (character) =>
    isHidden(character) ? hiddenCharacterMarker(character) : character,
  );
}

/** True when the text holds a character that would display differently from what runs. */
export function holdsHiddenCharacters(text: string): boolean {
  for (const character of text) if (isHidden(character)) return true;
  return false;
}

/** The text in order, each hidden character apart as its marker, so a view can set the markers off. */
export function requestTextParts(
  text: string,
): ReadonlyArray<{ readonly hidden: boolean; readonly text: string }> {
  const parts: Array<{ readonly hidden: boolean; readonly text: string }> = [];
  let plain = "";
  for (const character of text) {
    if (isHidden(character)) {
      if (plain) parts.push({ hidden: false, text: plain });
      plain = "";
      parts.push({ hidden: true, text: hiddenCharacterMarker(character) });
    } else plain += character;
  }
  if (plain) parts.push({ hidden: false, text: plain });
  return parts;
}
